import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import SearchBar   from '../components/SearchBar'
import FilterBar   from '../components/FilterBar'
import FAQCard     from '../components/FAQCard'
import FAQForm     from '../components/FAQForm'
import LoginForm   from '../components/LoginForm'
import SignUpForm  from '../components/SignUpForm'
import Navbar      from '../components/Navbar'
import CSVUploader from '../components/CSVUploader'
import FAQAI       from '../components/FAQAI'

import {
  fetchFaqs,
  fetchFilters,
  getCurrentUser,
} from '../utils/api'

export default function Home({ branding: companyBranding, onLogout }) {
  const navigate = useNavigate()

  // 1) filters & data
  const [search,      setSearch]      = useState('')
  const [language,    setLanguage]    = useState('')
  const [country,     setCountry]     = useState('')
  const [category,    setCategory]    = useState('')
  const [faqs,        setFaqs]        = useState([])
  const [loadingFaqs, setLoadingFaqs] = useState(false)

  // Pagination state
  const [page, setPage] = useState(1)
  const pageSize = 12
  const totalPages = Math.ceil(faqs.length / pageSize)
  const paginatedFaqs = faqs.slice((page - 1) * pageSize, page * pageSize)

  // dynamic filter‐options
  const [languages,  setLanguages]  = useState([])
  const [countries,  setCountries]  = useState([])
  const [categories, setCategories] = useState([])

  // 2) auth
  const [user,        setUser]        = useState(null)
  const [loadingUser, setLoadingUser] = useState(true)

  // 3) UI toggles
  const [showUploader,   setShowUploader]   = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showSignup,     setShowSignup]     = useState(false)

  const isAdmin = user?.role === 'admin'

  // A) Fetch current user on mount
  useEffect(() => {
    ;(async () => {
      try {
        const u = await getCurrentUser()
        setUser(u)
      } catch {
        // not logged in yet
      } finally {
        setLoadingUser(false)
      }
    })()
  }, [])

  // B) Fetch dynamic filters once user is present
  useEffect(() => {
    if (!user) return
    fetchFilters()
      .then(({ languages, countries, categories }) => {
        setLanguages(languages)
        setCountries(countries)
        setCategories(categories)
      })
      .catch(err => console.error('Failed to load filters:', err))
  }, [user])

  // C) Fetch FAQs when user or filters change
  useEffect(() => {
    if (!user) return
    ;(async () => {
      setLoadingFaqs(true)
      try {
        const data = await fetchFaqs({ search, language, country, category })
        setFaqs(data)
        setPage(1)
      } catch {
        console.error('Failed to fetch FAQs')
      } finally {
        setLoadingFaqs(false)
      }
    })()
  }, [user, search, language, country, category])

  // Handlers (create / delete / update)
  const handleFaqCreated = newFaq => { setFaqs(f => [newFaq, ...f]); setShowCreateForm(false) }
  const handleFaqDeleted = id   => setFaqs(f => f.filter(x => x.id !== id))
  const handleFaqUpdated = upd  => setFaqs(f => f.map(x => x.id === upd.id ? upd : x))

  // Render
  if (loadingUser) {
    return <div className="container py-4"><p>Loading…</p></div>
  }
  if (!user) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center mb-4">
          <button
            className={`btn ${!showSignup ? 'btn-primary' : 'btn-outline-primary'} me-2`}
            onClick={() => setShowSignup(false)}>
            Log In
          </button>
          <button
            className={`btn ${showSignup ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setShowSignup(true)}>
            Sign Up
          </button>
        </div>
        {showSignup ? <SignUpForm onCreate={setUser}/> : <LoginForm onLogin={setUser}/>} 
      </div>
    )
  }

  return (
    <div className="home-page">
      <Navbar
        user={user}
        logoUrl={companyBranding?.logo_url}
        onShowUploader={() => setShowUploader(true)}
        onLogout={onLogout}
      />

      <div className="home-page_container container py-4">
        <SearchBar className="home-page_search" value={search} onChange={e => setSearch(e.target.value)} />

        <FilterBar
          className="home-page_filters"
          language={language} setLanguage={setLanguage}
          country={country} setCountry={setCountry}
          category={category} setCategory={setCategory}
          languages={languages}
          countries={countries}
          categories={categories}
        />

        {isAdmin && (
          <div className="home-page_csv-uploader">
            <CSVUploader show={showUploader} onHide={() => setShowUploader(false)} />
          </div>
        )}

        <div className="home-page_actions d-flex justify-content-between mb-2">
          <button className="home-page_toogle-form-btn btn btn-primary" onClick={() => setShowCreateForm(p => !p)}>
            {showCreateForm ? 'Hide FAQ Form' : 'Add New FAQ'}
          </button>
          {!isAdmin && <button className="home-page_pinned-btn btn btn-outline-secondary" onClick={() => navigate('/pinned')}>Your Pinned FAQs</button>}
        </div>

        {showCreateForm && (
          <div className="home-page_create-form">
            <FAQForm onFaqCreated={handleFaqCreated} />
          </div>
        )}

        {loadingFaqs ? (
          <div className="home-page_loading text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading FAQs…</span>
            </div>
          </div>
        ) : faqs.length > 0 ? (
          <>
            <div className="home-page_faq-list row">
              {paginatedFaqs.map(faq => (
                <div key={faq.id} className="home-page_faq-item col-md-6 mb-4">
                  <FAQCard
                    faq={faq}
                    currentUser={user}
                    onFaqDeleted={handleFaqDeleted}
                    onFaqUpdated={handleFaqUpdated}
                  />
                </div>
              ))}
            </div>
            <nav>
              <ul className="pagination justify-content-center">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => Math.max(p-1,1))}>Previous</button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i+1).map(num => (
                  <li key={num} className={`page-item ${page === num ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(num)}>{num}</button>
                  </li>
                ))}
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => Math.min(p+1,totalPages))}>Next</button>
                </li>
              </ul>
            </nav>
          </>
        ) : (
          <p className="home-page_empty-state text-muted">No FAQs found for the selected filters.</p>
        )}
      </div>
      <FAQAI companyId={user.company_id} />
    </div>
  )
}